/**
 * templateDefinitions.ts — Catálogo de tipos de modelo do Studio
 *
 * Define, por tipo de documento, quais dados ficam disponíveis, quais formas de
 * entrega são permitidas e quais CAPACIDADES o modelo precisa entregar para
 * poder ser publicado.
 *
 * Importante: tipo (natureza do documento) é diferente de saída (meio de
 * entrega). Uma proposta pode sair em web e PDF; um e-mail só em e-mail.
 *
 * RELEASE: v1.1.0 - 2026-09-10 - Novo tipo `online_proposal` (Proposta Online) e
 *          marcação `creatable` para limitar os tipos oferecidos na criação.
 * RELEASE: v1.0.0 - 2026-09-09 - Versão inicial (Studio, Fase 1)
 */


import type { StudioTemplateDefinition, StudioTemplateType } from './types';

const registry = new Map<StudioTemplateType, StudioTemplateDefinition>();

/** Registra (ou substitui) a definição de um tipo de modelo. */
export function registerTemplateDefinition(definition: StudioTemplateDefinition): void {
  registry.set(definition.type, definition);
}

/** Retorna a definição de um tipo de modelo. */
export function getTemplateDefinition(
  type: StudioTemplateType,
): StudioTemplateDefinition | undefined {
  return registry.get(type);
}

/** Lista todos os tipos de modelo registrados. */
export function listTemplateDefinitions(): StudioTemplateDefinition[] {
  return Array.from(registry.values());
}

/**
 * Lista apenas os tipos oferecidos ao criar um novo modelo.
 * Tipos antigos continuam registrados (para exibir modelos já criados), mas
 * ficam fora do formulário de criação.
 */
export function listCreatableTemplateDefinitions(): StudioTemplateDefinition[] {
  return Array.from(registry.values()).filter((d) => d.creatable === true);
}

registerTemplateDefinition({
  type: 'proposal',
  label: 'Proposta',
  contexts: ['tenant', 'proposal', 'deal', 'customer', 'contact', 'seller'],
  outputs: ['web', 'pdf'],
  requiresCapabilities: ['proposal.items', 'proposal.total'],
  creatable: true,
  hint: 'Documento da proposta, usado na geração do PDF.',
});

// Proposta Online: mesma proposta, porém em formato de site. É entregue pelo
// link privado do negócio (token), NUNCA por endereço público indexável.
registerTemplateDefinition({
  type: 'online_proposal',
  label: 'Proposta Online',
  contexts: ['tenant', 'proposal', 'deal', 'customer', 'contact', 'seller', 'page'],
  outputs: ['web'],
  requiresCapabilities: [],
  creatable: true,
  hint: 'Proposta com cara de site, aberta pelo link privado enviado ao cliente (sem indexação).',
});

registerTemplateDefinition({
  type: 'quote',
  label: 'Orçamento',
  contexts: ['tenant', 'proposal', 'deal', 'customer', 'contact', 'seller'],
  outputs: ['web', 'pdf'],
  requiresCapabilities: [],
});

registerTemplateDefinition({
  type: 'landing_page',
  label: 'Landing Page',
  contexts: ['tenant', 'page', 'contact'],
  outputs: ['web'],
  requiresCapabilities: [],
  creatable: true,
  hint: 'Página pública com endereço próprio, aberta a qualquer visitante e indexável.',
});

registerTemplateDefinition({
  type: 'email',
  label: 'E-mail',
  contexts: ['tenant', 'contact', 'deal', 'seller'],
  outputs: ['email'],
  requiresCapabilities: [],
});

registerTemplateDefinition({
  type: 'report',
  label: 'Relatório',
  contexts: ['tenant'],
  outputs: ['web', 'pdf'],
  requiresCapabilities: [],
});

